import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Container, Row, Col, Tooltip } from 'reactstrap';
import Swipeable from 'react-swipeable';
import { spring, Motion } from 'react-motion';
import { FormattedDate } from 'react-intl';

// date-fns
import getFirstDateOfWeek from 'date-fns/start_of_week';
import addDays from 'date-fns/add_days';
import getMonth from 'date-fns/get_month';
import subWeeks from 'date-fns/sub_weeks';
import addWeeks from 'date-fns/add_weeks';
import isSameDay from 'date-fns/is_same_day';
import isBefore from 'date-fns/is_before';
import isSameMonth from 'date-fns/is_same_month';

// Components
import { Icon } from 'components/atoms/Icon/';
import CalendarWeek from 'components/molecules/CalendarWeek';
import FilterDaysBy from 'components/molecules/FilterDaysBy';
import LoaderHOC from 'components/atoms/LoaderHOC';
import ClutchErrorModal from 'components/pages/components/ClutchValet/ClutchError';

// Constants
import { TRANSPORTATION } from 'constants/transportation';
import { toISODate } from 'utils/string';

import CalendarLoader from './CalendarLoader';
import './Calendar.scss';

// Utils
import { clickEvent } from 'utils/adaConfigurations/customEvents';
const ns = 'calendar';
const { LOANER } = TRANSPORTATION;

class Calendar extends Component {
  constructor(props) {
    super(props);
    this.onSelect = this.onSelect.bind(this);
    this.onSelectDate = this.onSelectDate.bind(this);
    this.prev = this.prev.bind(this);
    this.next = this.next.bind(this);
    this.swiped = this.swiped.bind(this);
    this.swipingLeft = this.swipingLeft.bind(this);
    this.swipingRight = this.swipingRight.bind(this);
    this.toggleTooltip = this.toggleTooltip.bind(this);
    const { appointmentMonthDate } = this.props;
    this.clutchErrorModal = React.createRef();
    this.state = {
      calendarHorizontalSwiped: 0,
      selectedDate: null,
      monthDate: appointmentMonthDate,
      showTooltipLeft: false,
      showTooltipRight: false
    };
  }

  componentDidUpdate() {
    const { monthDate, selectedDate } = this.state;
    const {
      setStartTime,
      setTeamIds,
      onSelect,
      calendarSelectedDate,
      selectedDay,
      appointmentMonthDate,
      externalLoaner,
      transportationOption,
      errorLoadingClutchSlots,
      errorLoanerNotAvailable,
      requestFailure
    } = this.props;
    const monthDateProp = this.props.monthDate;
    const isClutchLoanerError =
      transportationOption === LOANER &&
      !!externalLoaner &&
      (errorLoadingClutchSlots || requestFailure || errorLoanerNotAvailable);
    this.clutchErrorModal.current.setShowModal(isClutchLoanerError);
    if (!monthDate && monthDateProp) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ monthDate: monthDateProp, selectedDate: monthDateProp });
      onSelect(monthDateProp);
    } else {
      const calendarSelectedDateString = calendarSelectedDate.date
        ? toISODate(calendarSelectedDate.date)
        : null;
      const selectedDayString = selectedDay ? selectedDay.calendarDate : null;
      if (calendarSelectedDate.forceUpdate) {
        this.onSelect(calendarSelectedDate.date);
        setTeamIds(null);
        setStartTime(null);
      } else if (calendarSelectedDateString !== selectedDayString) {
        this.onSelect(calendarSelectedDate.date);
      }
      const monthDateString = monthDate ? toISODate(monthDate) : null;
      const apptMonthDateString = appointmentMonthDate
        ? toISODate(appointmentMonthDate)
        : null;
      if (apptMonthDateString && monthDateString !== apptMonthDateString) {
        // eslint-disable-next-line react/no-did-update-set-state
        this.setState({ monthDate: appointmentMonthDate });
      }
      if (!selectedDate && calendarSelectedDate.date) {
        // eslint-disable-next-line react/no-did-update-set-state
        this.onSelect(calendarSelectedDate.date);
      }
    }
  }

  swipingLeft(e, absX) {
    this.setState({
      calendarHorizontalSwiped: -absX
    });
  }

  swipingRight(e, absX) {
    this.setState({
      calendarHorizontalSwiped: absX
    });
  }

  swiped(e, deltaX, deltaY, isFlick, velocity) {
    const { calendarHorizontalSwiped } = this.state;
    if (calendarHorizontalSwiped < -200) {
      this.setState({
        calendarHorizontalSwiped: -500
      });
      this.next();
    } else if (calendarHorizontalSwiped > 200) {
      this.setState({
        calendarHorizontalSwiped: 500
      });
      this.prev();
    } else {
      this.setState({
        calendarHorizontalSwiped: 0
      });
    }
  }

  prev() {
    this.monthNav(-1);
  }

  next() {
    this.monthNav(1);
  }

  async monthNav(diff) {
    const {
      getAvailableTimesByDates,
      gtmUserEvent,
      transportationOption,
      externalLoaner,
      errorLoadingClutchSlots,
      errorLoanerNotAvailable
    } = this.props;
    const { monthDate } = this.state;
    const isClutchLoanerError =
      transportationOption === LOANER &&
      !!externalLoaner &&
      (errorLoadingClutchSlots || errorLoanerNotAvailable);
    let minDate;
    let date = getFirstDateOfWeek(monthDate || new Date());
    let endDate;
    let eventResult;
    let eventElement = 'Calendar Navigation Button';
    let eventLocation = 'Calendar Section';
    if (diff === 1) {
      date = addWeeks(date, 2);
      eventResult = 'Change to the following weeks';
      if (isClutchLoanerError) {
        eventElement = 'Clutch Loaner Error Modal button';
        eventLocation = 'Clutch Loaner Error Modal';
      }
    } else {
      minDate = getFirstDateOfWeek(new Date());
      if (isSameDay(minDate, date)) {
        this.setState({
          calendarHorizontalSwiped: 0
        });
        return;
      }
      date = subWeeks(date, 2);
      if (isBefore(date, minDate)) {
        date = minDate;
      }
      eventResult = 'Change to the previous weeks';
    }
    try {
      this.setState({
        monthDate: date,
        calendarHorizontalSwiped: 0
      });
      endDate = addDays(date, 13);
      await getAvailableTimesByDates(date, endDate);
      gtmUserEvent(
        eventElement,
        'Clicked',
        eventResult,
        `GTM_CURRENT_PAGE > ${eventLocation}`
      );
    } catch (e) {
      /* eslint-disable no-console */
      console.error(e);
      /* eslint-enable no-console */
    }
  }

  toggleTooltip(tooltip) {
    this.setState({
      [`showTooltip${tooltip}`]: !this.state[`showTooltip${tooltip}`]
    });
  }

  onSelect(selectedDate, scrollDown) {
    const { onSelect, setAppointmentSelectedDay, getAvailableDay } = this.props;
    const day = getAvailableDay(selectedDate);
    onSelect(selectedDate, scrollDown);
    setAppointmentSelectedDay(day);
    this.setState({ selectedDate });
  }

  onSelectDate(selectedDate) {
    this.onSelect(selectedDate, true);
  }

  getHeader() {
    const {
      intl: { formatDate }
    } = this.props;
    const startOfWeek = getFirstDateOfWeek(new Date());
    let i;
    const days = [];
    for (i = 0; i < 7; i += 1) {
      days.push(addDays(startOfWeek, i));
    }
    return (
      <Row className={`${ns}__weekdays no-gutters`} id="calender_weekdays">
        {days.map(day => {
          return (
            <Col key={day}>
              {formatDate(day, {
                weekday: 'narrow'
              })}
            </Col>
          );
        })}
      </Row>
    );
  }

  getWeeks() {
    const { selectedDate, monthDate } = this.state;
    const weeks = [];
    let firstDateOfWeek = getFirstDateOfWeek(monthDate || new Date());
    let i;

    for (i = 0; i < 2; i += 1) {
      weeks.push(
        <CalendarWeek
          key={firstDateOfWeek}
          startDate={firstDateOfWeek}
          monthIdx={getMonth(firstDateOfWeek)}
          selectedDate={selectedDate}
          onSelect={this.onSelectDate}
        />
      );
      firstDateOfWeek = addWeeks(firstDateOfWeek, 1);
    }

    return weeks;
  }

  calendarComponent = (monthDate, calendarHorizontalSwiped, endDate) => {
    const date = getFirstDateOfWeek(monthDate || new Date());
    const minDate = getFirstDateOfWeek(new Date());
    const { intl } = this.props;
    const monthDateAriaLabel = intl.formatDate(monthDate || new Date(), {
      month: 'long'
    });
    const endDateAriaLabel = !isSameMonth(monthDate, endDate)
      ? intl.formatDate(endDate, {
          month: 'long'
        })
      : '';

    return (
      <Row
        className={`${ns}__row no-gutters`}
        tabIndex={0}
        aria-label={intl.formatMessage({
          id: 'c8.aria.select.date.of.appointment',
          defaultMessage: 'Select date of appointment'
        })}
      >
        <Col xs="12" sm="10" className={`${ns}__col`}>
          <div className={`${ns}__grid`}>
            <Row className="no-gutters">
              <Col xs="4">
                <div className={`${ns}__month-lbl text-left`}>
                  {monthDate && (
                    <div
                      className={`${ns}__month-lbl--fit`}
                      aria-label={`${monthDateAriaLabel} ${endDateAriaLabel}`}
                      tabIndex={0}
                    >
                      <FormattedDate value={monthDate} month={'short'} />
                      {!isSameMonth(monthDate, endDate) ? (
                        <span>
                          {' - '}
                          <FormattedDate value={endDate} month={'short'} />
                        </span>
                      ) : (
                        ''
                      )}
                    </div>
                  )}
                </div>
              </Col>
              <Col xs="8">
                <FilterDaysBy />
              </Col>
            </Row>
          </div>
          <div className={`${ns}__grid`}>{this.getHeader()}</div>
          <Swipeable
            onSwipingLeft={this.swipingLeft}
            onSwipingRight={this.swipingRight}
            onSwiped={this.swiped}
          >
            <div className={`${ns}__swipeable-container`}>
              <Motion
                defaultStyle={{ left: 0 }}
                style={{ left: spring(calendarHorizontalSwiped) }}
              >
                {style => (
                  <Container
                    fluid={true}
                    className={`${ns}__grid ${ns}__weekly`}
                    style={style}
                    id="calender_dates"
                  >
                    {this.getWeeks()}
                  </Container>
                )}
              </Motion>
            </div>
          </Swipeable>
        </Col>
        <Col
          sm="1"
          className={`${ns}__icon-container next__icon d-none d-sm-block`}
        >
          <div
            onClick={this.next}
            className={`${ns}__icon`}
            id={'tooltipRight'}
            onKeyUp={clickEvent}
          >
            <Icon
              type="chevron__right"
              className={`${ns}__icon`}
              aria-label={intl.formatMessage({
                id: 'c8.aria.show.next.two.weeks',
                defaultMessage: 'Show next two weeks button'
              })}
              tabIndex={0}
            />
          </div>
          <Tooltip
            className={`${ns}__tooltip`}
            placement={'top-start'}
            target={'tooltipRight'}
            isOpen={this.state.showTooltipRight}
            toggle={() => this.toggleTooltip('Right')}
          >
            {intl.formatMessage({
              id: 'c8.appointment.page.calendar.tooltip.next.two.weeks'
            })}
          </Tooltip>
        </Col>
        <Col
          sm="1"
          className={`${ns}__icon-container prev__icon d-none d-sm-block`}
        >
          <div hidden={isSameDay(minDate, date)}>
            <div
              onClick={this.prev}
              className={`${ns}__icon`}
              id={'tooltipLeft'}
              onKeyUp={clickEvent}
            >
              <Icon
                type="chevron__left"
                className={`${ns}__icon`}
                aria-label={intl.formatMessage({
                  id: 'c8.aria.show.previous.two.weeks',
                  defaultMessage: 'Show previous two weeks button'
                })}
                tabIndex={0}
              />
            </div>
            <Tooltip
              className={`${ns}__tooltip`}
              placement={'top-end'}
              target={'tooltipLeft'}
              isOpen={this.state.showTooltipLeft}
              toggle={() => this.toggleTooltip('Left')}
            >
              {intl.formatMessage({
                id: 'c8.appointment.page.calendar.tooltip.previous.two.weeks'
              })}
            </Tooltip>
          </div>
        </Col>
      </Row>
    );
  };

  render() {
    const { className } = this.props;
    const { monthDate, calendarHorizontalSwiped } = this.state;
    const classProps = classnames(ns, className);
    const firstDateOfWeek = getFirstDateOfWeek(monthDate);
    const endDate = addDays(firstDateOfWeek, 13);
    return (
      <div className={classProps}>
        <ClutchErrorModal
          key="clutchError"
          modalRef={this.clutchErrorModal}
          tryAgainCallback={this.next}
        />
        <div className={`${ns}__grid`}>
          <LoaderHOC
            serviceId={'firstAvailable|availableTimes|apptOptions'}
            placeHolderType={() => <CalendarLoader />}
            component={() =>
              this.calendarComponent(
                monthDate,
                calendarHorizontalSwiped,
                endDate
              )
            }
          />
        </div>
      </div>
    );
  }
}

Calendar.propTypes = {
  appointmentMonthDate: PropTypes.instanceOf(Date),
  calendarSelectedDate: PropTypes.object,
  className: PropTypes.string,
  filteredAvailableDays: PropTypes.array,
  firstAvailableTimeSlot: PropTypes.instanceOf(Date),
  getAvailableDay: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
  monthDate: PropTypes.instanceOf(Date),
  onSelect: PropTypes.func,
  selectedDay: PropTypes.object,
  setAppointmentSelectedDay: PropTypes.func.isRequired,
  setStartTime: PropTypes.func.isRequired,
  setTeamIds: PropTypes.func.isRequired,
  startTime: PropTypes.instanceOf(Date),
  errorLoadingClutchSlots: PropTypes.bool,
  errorLoanerNotAvailable: PropTypes.bool,
  requestFailure: PropTypes.bool,
  externalLoaner: PropTypes.string,
  transportationOption: PropTypes.string
};

Calendar.defaultProps = {
  onSelect: () => {}
};

export default Calendar;



// WEBPACK FOOTER //
// ./src/components/organisms/Calendar/Calendar.js